import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { QuestionCategories } from '~/types/generalQuestions';
import { settingsServiceUrl } from '~/utils/env';
import type { GeneralAnswer, GeneralQuestion, IQuestionnaireSettingsDto } from '~/types/generalQuestions';

const SETTINGS_BASE_URL = `${settingsServiceUrl()}/company-portal/settings`;
const RENTAL_APPLICATION_QUESTIONNAIRE_SETTINGS_URL = `${SETTINGS_BASE_URL}/rentalapplicationquestionnaire`;
const RENTAL_APPLICATION_QUESTIONNAIRE_COMPANY_SETTINGS_URL = `${RENTAL_APPLICATION_QUESTIONNAIRE_SETTINGS_URL}/company`;

type AnswerWithLegacyFlag = Omit<GeneralAnswer, 'id' | 'followUpQuestions' | 'isFreeFormInput'> & {
  id?: string;
  followUpQuestions?: QuestionWithLegacyFlag[];
  isFreeFormInput?: boolean;
  isFreeformInput?: boolean;
};

type QuestionWithLegacyFlag = Omit<GeneralQuestion, 'id' | 'answers'> & {
  id?: string;
  answers?: AnswerWithLegacyFlag[];
};

const isFreeFormInput = (answer: AnswerWithLegacyFlag): boolean => answer.isFreeFormInput ?? answer.isFreeformInput ?? false;

const assignUniqueIdsToAnswers = (answers: AnswerWithLegacyFlag[]): GeneralAnswer[] =>
  answers.map((answer) => ({
    ...answer,
    id: crypto.randomUUID(),
    isFreeFormInput: isFreeFormInput(answer),
    followUpQuestions: assignUniqueIds(answer.followUpQuestions ?? []),
  }));

const assignUniqueIds = (questions: QuestionWithLegacyFlag[]): GeneralQuestion[] =>
  questions.map((question) => ({
    ...question,
    id: crypto.randomUUID(),
    answers: assignUniqueIdsToAnswers(question.answers ?? []),
    isMultiSelect: question.isMultiSelect,
  }));

const rawToMap = (raw: Record<string, unknown>): Map<QuestionCategories, GeneralQuestion[]> => {
  const map = new Map<QuestionCategories, GeneralQuestion[]>();
  Object.entries(raw).forEach(([category, questions]) => {
    if (Array.isArray(questions)) {
      map.set(category as QuestionCategories, assignUniqueIds(questions));
    }
  });
  return map;
};

export class RentalApplicationQuestionnaireSettingsRepository extends BaseRestRepository {
  public async getRentalApplicationQuestionnaireSettings(): Promise<IQuestionnaireSettingsDto> {
    const response = await this.fetchWithAuth(RENTAL_APPLICATION_QUESTIONNAIRE_COMPANY_SETTINGS_URL);
    const json = await this.getJsonResponse(response);
    const applicationQuestions =
      json.applicationQuestions && typeof json.applicationQuestions === 'object' && !Array.isArray(json.applicationQuestions)
        ? rawToMap(json.applicationQuestions as Record<string, unknown>)
        : new Map<QuestionCategories, GeneralQuestion[]>([[QuestionCategories.General, []]]);
    return { applicationQuestions };
  }

  public async deleteRentalApplicationQuestionnaireSettings(): Promise<void> {
    return undefined;
  }

  public async updateRentalApplicationQuestionnaireSettings(payload: IQuestionnaireSettingsDto): Promise<IQuestionnaireSettingsDto> {
    const response = await this.fetchWithAuth(RENTAL_APPLICATION_QUESTIONNAIRE_COMPANY_SETTINGS_URL, {
      method: 'POST',
      body: JSON.stringify({
        settings: {
          applicationQuestions: Object.fromEntries(payload.applicationQuestions),
        },
      }),
    });
    const json = await this.getJsonResponse(response);
    const applicationQuestions =
      json.applicationQuestions && typeof json.applicationQuestions === 'object' && !Array.isArray(json.applicationQuestions)
        ? rawToMap(json.applicationQuestions as Record<string, unknown>)
        : new Map<QuestionCategories, GeneralQuestion[]>([[QuestionCategories.General, []]]);
    return { applicationQuestions };
  }
}
